import "./MowerArea.scss";
import clsx from "clsx";

/* eslint-disable react/style-prop-object */
import { Box, FormCheckbox, Tooltip, Typography } from "@husqvarna/ui-core";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { getTotalAreaForUnitSystem, getLengthForUnitSystem, getCircumferenceForArea } from "../../../../../../../shared/utils";
import { Area } from "../../../../../../models/Area";
import { UnitSystem, CuttingQuality } from "../../../../../../models/Enums";
import { RootState } from "../../../../../../../shared/store";
import { clearAreaHoverValues, setAreaHover } from "../../../../../../../shared/store/areaHoverSlice";

export type MowerAreaProps = {
  isDisabled?: boolean;
  isSelected?: boolean;
  isSaved?: boolean;
  isCovered?: boolean;
  showCapacityError?: boolean;
  showQualityError?: boolean;
  showSlopeError?: boolean;
  area: Area,
  onClicked?: () => void;
  unitSystem: UnitSystem
};

const MowerArea = ({
  isDisabled,
  isSelected,
  isSaved,
  isCovered,
  showCapacityError,
  showQualityError,
  showSlopeError,
  area,
  onClicked,
  unitSystem
}: MowerAreaProps) => {
  const intl = useIntl();
  const [coveragePercentage, setCoveragePercentage] = useState(0);
  const areaCoverageValues = useSelector((state: RootState) => state.equipmentEdit.areaCoverageValues);
  const mowerAreaContainer = useRef<HTMLElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    // Always prioritize areaCoverageValues from Redux (calculated in MowerDialog)
    // This ensures we show the correct coverage even when backend updates area.coveragePercent with delay
    // If areaCoverageValues exists (even if empty), we should use it to avoid flickering when backend updates
    if (areaCoverageValues !== null && areaCoverageValues !== undefined) {
      // Use value from areaCoverageValues if it exists, otherwise use 0 (not area.coveragePercent)
      // This prevents flickering when backend updates area.coveragePercent before areaCoverageValues is recalculated
      setCoveragePercentage(areaCoverageValues[area.id] ?? 0);
    } else {
      // Only use area.coveragePercent as fallback if areaCoverageValues doesn't exist at all
      setCoveragePercentage(area.coveragePercent);
    }
  }, [areaCoverageValues, area]);

  const getCuttingQualityName = (cuttingQuality : CuttingQuality) : string => {
    if (cuttingQuality === CuttingQuality.Regular) return intl.formatMessage({id:'general.cuttingQuality.max72', defaultMessage: 'Max 72'});
    if (cuttingQuality === CuttingQuality.Pro) return intl.formatMessage({id:'general.cuttingQuality.standard48', defaultMessage: 'Standard 48'});
    return intl.formatMessage({id:'general.cuttingQuality.sport24', defaultMessage: 'Sport 24'});
  }

  useEffect(() => {
    if(mowerAreaContainer.current){
      mowerAreaContainer.current.onmouseover = (e) => {
        setTooltipVisible(true);
        dispatch(setAreaHover([area.id]))
      };

      mowerAreaContainer.current.onmouseleave = (e) => {
        setTooltipVisible(false);
        dispatch(clearAreaHoverValues())
      };
    }
  }, []);

  const getTextColor = (isHeadline?: boolean) => {
    if (isDisabled) {
      return "Grey400";
    }
    
    return isHeadline ? "Grey600" : "Grey500";
  }
  
  // NOTE: the FormCheckbox has disabled={true} for a reason. It's there to prevent a bug where the onClick event on the parent Box (line 68) is triggered twice
  // when the user clicks directly on the FormCheckbox. By setting FormCheckbox to disabled, the FormCheckbox stops listening to click events, meaning that the
  // parent Box handles all click events and the bug disappears.
  return (
    <>
      <Box className="tcsp-mower-area-container" flexDirection="column" ref={mowerAreaContainer}
              onClick={onClicked}>
        <Tooltip
          toolTipText={intl.formatMessage({
            id: "project.equipment.mowerDialog.mowerArea.slopeError",
            defaultMessage: "The selected mower can't manage the slope.",
          })}
          visible={tooltipVisible && showSlopeError}
        >
            <Box
              container
              br={3}
              pl={2}
              py={2}
              className={clsx("tcsp-mower-area", {
                "tcsp-mower-area--selected": isSelected,
                "tcsp-mower-area--wrong": showCapacityError || showQualityError,
                "tcsp-mower-area--disabled": isDisabled,
                "tcsp-mower-area--saved": isSaved && isSelected,
                "tcsp-mower-area--covered": isCovered,
              })}
              alignItems="flex-start"
              justifyContent="space-between"
              gap={1}
            >
              <FormCheckbox
                checked={isSelected}
                disabled={true}
                aria-label={area.name}
              />
              <Box>
                <Box flexDirection="column" gap={1} mb={1} pr={2}>
                  <Typography
                    variant="Microcopy"
                    className="area-name"
                    text={area.name}
                    tag="h2"
                    color={getTextColor(true)}
                  />
                  <Typography variant="Microcopy" color={getTextColor(true)} className="area-coverage">
                    <FormattedMessage
                      id="projects.equipment.mowerDialog.mowerArea.coverageStatus"
                      defaultMessage="{coveragePercentage} covered of {size}"
                      values={{
                        coveragePercentage: (
                          <FormattedNumber
                            value={coveragePercentage}
                            maximumFractionDigits={0}
                            style="unit"
                            unit="percent"
                          />
                        ),
                        size: getTotalAreaForUnitSystem(area.size, unitSystem),
                      }}
                    />
                  </Typography>
                </Box>
                <Box className="area-properties-list">
                  <Typography
                    variant="Microcopy"
                    tag="p" color={getTextColor()}
                    text={getCuttingQualityName(area.cuttingQuality)}
                  />
                  <Box className="area-property-separator" />
                  <Typography variant="Microcopy" tag="p" color={getTextColor()}>
                    <FormattedMessage
                      id="projects.equipment.mowerDialog.mowerArea.capacity"
                      defaultMessage="Complexity: {complexityPercentage}%"
                      values={{
                        complexityPercentage: area.complexityPercentage || 0,
                      }}
                    />
                  </Typography>
                  <Box className="area-property-separator" />
                  <Typography variant="Microcopy" tag="p" color={getTextColor()}>
                    <FormattedMessage
                      id="projects.equipment.mowerSelection.mowerArea.slopePercentage"
                      defaultMessage="{slope}% slope"
                      values={{ slope: area.maxSlope }}
                    />
                  </Typography>
                  <Box className="area-property-separator" />
                  <Typography variant="Microcopy" tag="p" color={getTextColor()}>
                    <FormattedMessage
                      id="projects.equipment.mowerDialog.mowerArea.availableHours"
                      defaultMessage="Available {availableHours}h"
                      values={{
                        availableHours: area.availableHoursToCutPerDay,
                      }}
                    />
                  </Typography>
                  <Box className="area-property-separator" />
                  <Typography variant="Microcopy" tag="p" color={getTextColor()}>
                    <FormattedMessage
                      id="projects.equipment.mowerDialog.mowerArea.circumference"
                      defaultMessage="Circumference {circumference}"
                      values={{
                        circumference: getLengthForUnitSystem(
                          getCircumferenceForArea(area),
                          unitSystem
                        ),
                      }}
                    />
                  </Typography>
                </Box>
              </Box>
            </Box>
        </Tooltip>
          <Box height={2}>
          </Box>
      </Box>
    </>
  );
};

export default MowerArea;
